import React, { useState } from 'react';
import { Button, Card, Input, Label, Spinner } from 'reactstrap';
import CustomModal from '../../../../../../../../../Components/Modals/CustomModal';
import SaveGoogleDriveAssetService from '../../../../../../../../../../Service/DashboardServices/AssetGalleryServices/SaveGoogleDriveAssetService';
import notify from '../../../../../../../../../../Notifications/ToastifyActions';

const SERVICE_ACCOUNT_EMAIL = "assetgallery@assetgallery-502705.iam.gserviceaccount.com";
const SERVICE_ACCOUNT_ACCESS_HINT = (
  `Before entering the Google Drive folder link, please ensure that ${SERVICE_ACCOUNT_EMAIL} has been granted access to the corresponding folder.`
);

function GoogleDriveAssetsImport({
  token,
  isLinkPopupOpen,
  setIsLinkPopupOpen,
  integerId,
  onImportSuccess,
}) {
  const [driveLink, setDriveLink] = useState("");
  const [driveLinkError, setDriveLinkError] = useState("");
  const [driveLinkHint, setDriveLinkHint] = useState("");
  const [savingAssets, setSavingAssets] = useState(false);

  const resetLinkState = () => {
    setDriveLink("");
    setDriveLinkError("");
    setDriveLinkHint("");
  };

  const closeLinkPopup = () => {
    if (savingAssets) return;
    setIsLinkPopupOpen(false);
    resetLinkState();
  };

  const showPopupError = (res) => {
    const message =
      (res && (res.message || res.error)) ||
      "Unable to save Google Drive assets. The live server may be missing GOOGLE_DRIVE_CREDENTIALS in settings or the SaveGoogleDriveAssets API is not deployed.";
    let hint = (res && res.hint) || "";
    if (res && res.errorCode === "CREDENTIALS_MISSING") {
      hint = "Ask devops to deploy settings.py with GOOGLE_DRIVE_CREDENTIALS and restart the live app.";
    } else if (res && res.errorCode === "AUTH_FAILED") {
      hint = "Service account key in GOOGLE_DRIVE_CREDENTIALS is invalid or expired. Update settings and redeploy.";
    } else if (res && (res.errorCode === "PERMISSION_DENIED" || res.errorCode === "FOLDER_NOT_FOUND")) {
      hint = SERVICE_ACCOUNT_ACCESS_HINT;
    } else if (!hint) {
      hint = SERVICE_ACCOUNT_ACCESS_HINT;
    }
    if (res && res.details) {
      hint = hint + " Details: " + String(res.details);
    }
    if (res && res.errorCode) {
      setDriveLinkError("[" + res.errorCode + "] " + message);
    } else {
      setDriveLinkError(message);
    }
    setDriveLinkHint(hint);
  };

  const handleSubmit = () => {
    if (!integerId) {
      setDriveLinkError("Store integerId is missing. Cannot save assets.");
      setDriveLinkHint("");
      return;
    }

    if (!driveLink || !driveLink.trim()) {
      setDriveLinkError("Please enter your Google Drive folder link.");
      setDriveLinkHint(SERVICE_ACCOUNT_ACCESS_HINT);
      return;
    }

    if (!driveLink.includes("drive.google.com") && !driveLink.includes("/folders/")) {
      setDriveLinkError("Please enter a valid Google Drive folder link.");
      setDriveLinkHint(SERVICE_ACCOUNT_ACCESS_HINT);
      return;
    }

    setDriveLinkError("");
    setDriveLinkHint("");
    setSavingAssets(true);

    const data = {
      folder_url: driveLink.trim(),
      integerId,
    };

    SaveGoogleDriveAssetService(token, data).then((res) => {
      setSavingAssets(false);
      if (res && res.status) {
        notify.NotifySuccess(res.message || "Google Drive assets saved successfully.");
        setIsLinkPopupOpen(false);
        resetLinkState();
        if (typeof onImportSuccess === "function") {
          onImportSuccess(res);
        }
      } else {
        showPopupError(res);
        if (res && res.message) {
          notify.NotifyError(res.message);
        }
      }
    }).catch(() => {
      setSavingAssets(false);
      showPopupError({
        message: "Something went wrong. Please try again.",
        hint: SERVICE_ACCOUNT_ACCESS_HINT,
      });
      notify.NotifyError("Something went wrong while saving Google Drive assets.");
    });
  };

  return (
    <CustomModal
      isOpen={isLinkPopupOpen}
      toggle={closeLinkPopup}
      size={"md"}
      header={'Import Assets'}
      className="curve-popup"
    >
      <Card className="curve-popup p-3 border-0 shadow-none">
        <Label className="mb-2"><b>Enter Google Drive folder link</b></Label>
        <p className="mb-3" style={{ fontSize: "13px", color: "#555" }}>
          Before entering the Google Drive folder link, please ensure that{" "}
          <b>{SERVICE_ACCOUNT_EMAIL}</b> has been granted access to the corresponding folder.
        </p>
        <Input
          type="text"
          placeholder="https://drive.google.com/drive/folders/..."
          value={driveLink}
          onChange={(e) => {
            setDriveLink(e.target.value);
            setDriveLinkError("");
            setDriveLinkHint("");
          }}
          disabled={savingAssets}
        />
        {driveLinkError && (
          <div
            className="mt-2 p-2"
            style={{
              background: "#fdecea",
              border: "1px solid #f5c2c0",
              borderRadius: 6,
            }}
          >
            <p className="text-danger mb-1" style={{ fontSize: "13px" }}>
              <b>{driveLinkError}</b>
            </p>
            {driveLinkHint && (
              <p className="mb-0" style={{ fontSize: "12px", color: "#842029" }}>
                {driveLinkHint}
              </p>
            )}
          </div>
        )}
        <div className="d-flex justify-content-end mt-3">
          <Button
            color="secondary"
            className="mr-2"
            disabled={savingAssets}
            onClick={closeLinkPopup}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={savingAssets}
            onClick={handleSubmit}
          >
            {savingAssets ? (
              <>
                <Spinner size="sm" className="mr-1" /> Saving...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </Card>
    </CustomModal>
  );
}

export default GoogleDriveAssetsImport;
